import React, { useEffect, useState } from "react";
import { api, fmtDate, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Printer, Link2, MapPin } from "lucide-react";
import StatusBadge from "@/components/crm/StatusBadge";
import { SearchBox, DeleteButton, matchesQuery } from "@/components/crm/RecordActions";
import FilterBar, { FilterSelect, inDateRange } from "@/components/crm/Filters";
import { PhotoGallery } from "@/components/crm/PhotoUpload";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const TYPES = ["Panel","Inverter","Wiring","Monitoring","Structure","Other"];
const PRIORITY = ["Low","Medium","High","Urgent"];
const STATUS = ["Open","Assigned","In Progress","Resolved","Closed"];
const empty = {
  customer_id:"", complaint_type:"Panel", priority:"Medium", description:"",
  technician_id:"", visit_date:"", resolution:"",
  photos:[], gps_lat:null, gps_lng:null,
  status:"Open",
};

export default function Complaints() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [fPriority, setFPriority] = useState("__all__");
  const [fStatus, setFStatus] = useState("__all__");
  const [fTech, setFTech] = useState("__all__");

  const load = async () => {
    setItems((await api.get("/complaints")).data);
    if (user.role !== "technician") {
      setCustomers((await api.get("/customers")).data);
      if (user.role === "admin") try{ setTechnicians((await api.get("/users")).data.filter(u=>u.role==="technician")); } catch {}
    }
  };
  useEffect(()=>{ load(); },[]);

  const save = async () => {
    try {
      if (editId) await api.patch(`/complaints/${editId}`, form);
      else await api.post("/complaints", form);
      toast.success("Saved");
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch(e){ toast.error(formatError(e.response?.data?.detail)); }
  };

  const edit = (c) => { setForm({...empty, ...c}); setEditId(c.id); setOpen(true); };

  const del = async (c) => {
    try { await api.delete(`/complaints/${c.id}`); toast.success(`Complaint ${c.complaint_no} deleted`); load(); }
    catch(e){ toast.error(formatError(e.response?.data?.detail)); }
  };

  const captureGps = () => {
    if (!navigator.geolocation) { toast.error("GPS not available"); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => { setForm(f => ({...f, gps_lat:p.coords.latitude, gps_lng:p.coords.longitude})); toast.success("GPS captured"); },
      () => toast.error("Unable to capture GPS"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const canCreate = user.role === "admin" || user.role === "sales";
  const visible = items
    .filter((c) => matchesQuery(c, search))
    .filter((c) => inDateRange(c.created_at, fFrom, fTo))
    .filter((c) => fPriority === "__all__" || c.priority === fPriority)
    .filter((c) => fStatus === "__all__" || c.status === fStatus)
    .filter((c) => fTech === "__all__" || c.technician_id === fTech);

  return (
    <div className="space-y-5" data-testid="complaints-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900" style={{fontFamily:'Outfit'}}>Complaints <span className="text-slate-400 text-lg font-medium">/ शिकायत</span></h1>
          <p className="text-slate-500 text-sm">Register, assign & resolve customer issues with photo & GPS proof.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SearchBox value={search} onChange={setSearch} placeholder="Search customer, complaint #..." testid="complaints-search-input" />
          <Button variant="outline" size="sm" onClick={()=>{navigator.clipboard.writeText(`${window.location.origin}/complaint-register`); toast.success("Complaint link copied — share with customers");}} data-testid="share-complaint-link-btn">
            <Link2 className="w-3.5 h-3.5 mr-1"/>Complaint Link
          </Button>
          <Dialog open={open} onOpenChange={(v)=>{setOpen(v); if(!v){setForm(empty); setEditId(null);}}}>
            {canCreate && (
            <DialogTrigger asChild>
              <Button className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="add-complaint-btn"><Plus className="w-4 h-4 mr-1"/>New Complaint</Button>
            </DialogTrigger>
            )}
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editId?"Update":"New"} Complaint</DialogTitle></DialogHeader>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <Label className="text-xs font-semibold text-slate-600">Customer*</Label>
                  <Select value={form.customer_id} onValueChange={v=>setForm({...form, customer_id:v})}>
                    <SelectTrigger data-testid="complaint-customer-select"><SelectValue placeholder="Select"/></SelectTrigger>
                    <SelectContent>{customers.map(c=><SelectItem key={c.id} value={c.id}>{c.name} — {c.mobile}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs font-semibold text-slate-600">Type</Label>
                  <Select value={form.complaint_type} onValueChange={v=>setForm({...form, complaint_type:v})}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>{TYPES.map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs font-semibold text-slate-600">Priority</Label>
                  <Select value={form.priority} onValueChange={v=>setForm({...form, priority:v})}>
                    <SelectTrigger data-testid="complaint-priority-select"><SelectValue/></SelectTrigger>
                    <SelectContent>{PRIORITY.map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs font-semibold text-slate-600">Assign Technician</Label>
                  <Select value={form.technician_id||""} onValueChange={v=>setForm({...form, technician_id:v})}>
                    <SelectTrigger data-testid="complaint-technician-select"><SelectValue placeholder="Select"/></SelectTrigger>
                    <SelectContent>{technicians.map(t=><SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs font-semibold text-slate-600">Status</Label>
                  <Select value={form.status} onValueChange={v=>setForm({...form, status:v})}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>{STATUS.map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs font-semibold text-slate-600">Visit Date</Label><Input type="date" value={form.visit_date||""} onChange={e=>setForm({...form, visit_date:e.target.value})}/></div>
                <div className="sm:col-span-2"><Label className="text-xs font-semibold text-slate-600">Description</Label><Textarea rows={2} value={form.description} onChange={e=>setForm({...form, description:e.target.value})}/></div>
                <div className="sm:col-span-2"><Label className="text-xs font-semibold text-slate-600">Resolution</Label><Textarea rows={2} value={form.resolution} onChange={e=>setForm({...form, resolution:e.target.value})}/></div>
              </div>

              <div className="mt-3 text-xs font-bold uppercase tracking-widest text-slate-500 border-b pb-1">Photos & GPS Location</div>
              <div className="mt-2"><PhotoGallery label="Complaint Photos" value={form.photos||[]} onChange={(arr)=>setForm({...form, photos:arr})} testid="complaint-photos" /></div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end mt-2">
                <div><Label className="text-xs font-semibold text-slate-600">Latitude</Label><Input type="number" step="0.000001" value={form.gps_lat??""} onChange={e=>setForm({...form, gps_lat:parseFloat(e.target.value)||null})} data-testid="complaint-gps-lat"/></div>
                <div><Label className="text-xs font-semibold text-slate-600">Longitude</Label><Input type="number" step="0.000001" value={form.gps_lng??""} onChange={e=>setForm({...form, gps_lng:parseFloat(e.target.value)||null})} data-testid="complaint-gps-lng"/></div>
                <Button type="button" variant="outline" onClick={captureGps} data-testid="complaint-capture-gps"><MapPin className="w-4 h-4 mr-1 text-emerald-600"/>Capture GPS</Button>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
                <Button className="bg-amber-500 hover:bg-amber-600" onClick={save} disabled={!form.customer_id||!form.description} data-testid="save-complaint-btn">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <FilterBar from={fFrom} to={fTo} onFrom={setFFrom} onTo={setFTo}
        onClear={()=>{setFFrom("");setFTo("");setFPriority("__all__");setFStatus("__all__");setFTech("__all__");setSearch("");}}>
        <FilterSelect value={fPriority} onChange={setFPriority} placeholder="Priority" testid="filter-priority"
          options={PRIORITY.map(p=>({value:p,label:p}))} />
        <FilterSelect value={fStatus} onChange={setFStatus} placeholder="Status" testid="filter-status"
          options={STATUS.map(s=>({value:s,label:s}))} />
        {user.role === "admin" && (
          <FilterSelect value={fTech} onChange={setFTech} placeholder="Technician" testid="filter-technician"
            options={technicians.map(t=>({value:t.id,label:t.name}))} />
        )}
      </FilterBar>

      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Comp #</TableHead><TableHead>Customer</TableHead>
            <TableHead>Type</TableHead><TableHead>Priority</TableHead>
            <TableHead>Status</TableHead><TableHead>Date</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {visible.length===0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400">{search ? `No complaints matching "${search}"` : "No complaints."}</TableCell></TableRow>}
            {visible.map(c=>(
              <TableRow key={c.id} data-testid={`complaint-row-${c.complaint_no}`}>
                <TableCell className="font-mono text-xs">{c.complaint_no}</TableCell>
                <TableCell className="font-medium">{c.customer_name}<div className="text-xs text-slate-500">{c.customer_mobile}</div></TableCell>
                <TableCell><span className="text-xs bg-slate-100 rounded-full px-2 py-0.5">{c.complaint_type}</span></TableCell>
                <TableCell><StatusBadge value={c.priority}/></TableCell>
                <TableCell><StatusBadge value={c.status}/></TableCell>
                <TableCell className="text-xs">{fmtDate(c.created_at)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={()=>edit(c)} data-testid={`edit-complaint-${c.complaint_no}`}>Edit</Button>
                    <Link to={`/complaints/${c.id}/print`}><Button size="sm" variant="outline" data-testid={`print-complaint-${c.complaint_no}`}><Printer className="w-3 h-3"/></Button></Link>
                    {user?.role === "admin" && (
                      <DeleteButton title={`Delete Complaint ${c.complaint_no}?`} description={`This will permanently delete this complaint for ${c.customer_name}. This action cannot be undone.`} onConfirm={()=>del(c)} testid={`delete-complaint-${c.complaint_no}`} />
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}
